import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import { ViewColumnsIcon } from '@heroicons/react/24/outline';
import { Pagination, Column, Table } from '@shared/components/Table';
import { SearchBar } from '@shared/components/SearchBar';
import { FilterBar } from '@shared/components/FilterBar';
import { FilterPopup } from '@shared/components/FilterPopup';
import {
  ColumnCustomizationPopup,
  type ColumnOption,
} from '@shared/components/ColumnCustomizationPopup';
import { ToggleButton } from '@shared/components/ToggleButton';
import { Tooltip } from '@shared/components/Tooltip';
import { Snackbar } from '@shared/components/Snackbar';
import { Menu, MenuVariant, MenuSize } from '@shared/components/Menu';
import { UpdateStatusDialog } from '@/epics/adminManagment/pages/adminList/UpdateStatusDialog';
import { getClientStatusDialogConfig } from './clientStatusDialogConfig';
import { useUpdateClientStatus } from '@epics/clientManagement/hooks/useUpdateClientStatus';
import { useClientList } from './useClientList';
import type { ClientListItem } from '@epics/clientManagement/types/clientListType';
import noDataAnimation from '@/assets/lottie/noDataAnimation.json';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getInitials = (name: string): string =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
function getProfileImageUrl(
  logoUrl: string | null | undefined
): string | undefined {
  return typeof logoUrl === 'string' && logoUrl.trim() !== ''
    ? logoUrl
    : undefined;
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  const formatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}/${get('month')}/${get('day')}, ${get('hour')}:${get('minute')}:${get('second')}`;
}

// ---------------------------------------------------------------------------
// Column customization config
// ---------------------------------------------------------------------------

const COLUMN_OPTIONS: ColumnOption[] = [
  { key: 'name', label: 'Imię i nazwisko', disabled: true },
  { key: 'user_id', label: 'ID klienta', disabled: true },
  { key: 'subscription_plan', label: 'Nazwa planu' },
  { key: 'payment_status', label: 'Status płatności', disabled: true },
  { key: 'created_at', label: 'Data utworzenia' },
  { key: 'status', label: 'Status', disabled: true },
];

const DEFAULT_VISIBLE_COLUMNS: Record<string, boolean> = Object.fromEntries(
  COLUMN_OPTIONS.map((o) => [o.key, true])
);

const COLUMNS_URL_PARAM = 'cols';
const DEFAULT_COLS_STRING = COLUMN_OPTIONS.map((o) => o.key).join(',');

function parseColsParam(value: string | null): Record<string, boolean> | null {
  if (!value) {
    return null;
  }
  const validKeys = new Set(COLUMN_OPTIONS.map((o) => o.key));
  const visibleKeys = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => validKeys.has(s));
  if (visibleKeys.length === 0) {
    return null;
  }
  const result: Record<string, boolean> = {};
  COLUMN_OPTIONS.forEach((o) => {
    result[o.key] = visibleKeys.includes(o.key);
  });
  return result;
}

function buildColsParam(visibleKeys: Record<string, boolean>): string {
  return COLUMN_OPTIONS.filter((o) => visibleKeys[o.key] !== false)
    .map((o) => o.key)
    .join(',');
}

// ---------------------------------------------------------------------------
// Payment status badge
// ---------------------------------------------------------------------------

function PaymentStatusBadge({ status }: { status: 'paid' | 'unpaid' | null }) {
  if (!status) {
    return (
      <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px]">
        —
      </span>
    );
  }
  const isPaid = status === 'paid';
  const label = isPaid ? 'Opłacone' : 'Oczekuje';
  const dotBg = isPaid ? 'bg-[#00AB76]' : 'bg-[#121F28]';
  const textColor = isPaid ? 'text-[#00AB76]' : 'text-[#121F28]';
  return (
    <Tooltip content={label}>
      <div className="inline-flex items-center overflow-hidden rounded-[8px] bg-[#E8EEF0] max-w-full">
        <div className="flex items-center gap-2 pl-2 pr-3 py-[6px] min-w-0">
          <div
            className={`flex items-center justify-center p-[2px] rounded-full shrink-0 ${dotBg}`}
          >
            <div className="w-[2px] h-[2px]" />
          </div>
          <span
            className={`text-[14px] font-medium leading-5 tracking-[0.1px] truncate ${textColor}`}
          >
            {label}
          </span>
        </div>
      </div>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ClientList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Snackbars
  const [showAddSuccessSnackbar, setShowAddSuccessSnackbar] = useState(false);
  const [showEditSuccessSnackbar, setShowEditSuccessSnackbar] = useState(false);

  // Filter popup
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Actions menu
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const menuPortalRef = useRef<HTMLDivElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const columnContainerRef = useRef<HTMLDivElement | null>(null);

  // Column customization
  const [isColumnPopupOpen, setIsColumnPopupOpen] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<
    Record<string, boolean>
  >(() => ({ ...DEFAULT_VISIBLE_COLUMNS }));

  // Restore column visibility from URL
  const colsParam = searchParams.get(COLUMNS_URL_PARAM);
  useEffect(() => {
    const parsed = parseColsParam(colsParam);
    setVisibleColumnKeys(parsed ?? { ...DEFAULT_VISIBLE_COLUMNS });
  }, [colsParam]);

  // Hook
  const {
    clients,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing,
    setSearch,
    dateRange,
    setDateRange,
    statusOptions,
    setStatusOptions,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    sortColumn,
    sortOrder,
    handleSort,
    refetchClients,
  } = useClientList();

  const {
    selectedClient,
    isDialogOpen,
    isSubmitting,
    handleOpenDialog,
    handleDialogClose,
    handleConfirm: handleConfirmStatusUpdate,
    showSuccessSnackbar: showStatusSuccessSnackbar,
    successMessage: statusSuccessMessage,
    setSuccessMessage: setStatusSuccessMessage,
    setShowSuccessSnackbar: setShowStatusSuccessSnackbar,
    showErrorSnackbar: showStatusErrorSnackbar,
    errorMessage: statusErrorMessage,
    setShowErrorSnackbar: setShowStatusErrorSnackbar,
  } = useUpdateClientStatus({ onSuccess: refetchClients });

  // Location state snackbars
  useEffect(() => {
    if (location.state?.showAddClientSuccess) {
      setShowAddSuccessSnackbar(true);
      window.history.replaceState({}, document.title);
    }
    if (location.state?.showEditClientSuccess) {
      setShowEditSuccessSnackbar(true);
      window.history.replaceState({}, document.title);
    }
    if (location.state?.successMessage) {
      setStatusSuccessMessage(location.state.successMessage as string);
      setShowStatusSuccessSnackbar(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, setStatusSuccessMessage, setShowStatusSuccessSnackbar]);

  // Close menu on outside click
  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = menuTriggerRef.current?.contains(target) ?? false;
      const insidePortal = menuPortalRef.current?.contains(target) ?? false;
      if (!insideTrigger && !insidePortal) {
        setOpenMenuRowId(null);
        setMenuPosition(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuRowId]);

  // Focus management for menu
  const prevOpenMenuRowIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (openMenuRowId !== null) {
      prevOpenMenuRowIdRef.current = openMenuRowId;
      requestAnimationFrame(() => {
        const firstItem =
          menuPortalRef.current?.querySelector<HTMLElement>(
            '[role="menuitem"]'
          );
        firstItem?.focus();
      });
    } else {
      const hadOpen = prevOpenMenuRowIdRef.current !== null;
      prevOpenMenuRowIdRef.current = null;
      if (hadOpen && menuTriggerRef.current) {
        requestAnimationFrame(() => menuTriggerRef.current?.focus());
      }
    }
  }, [openMenuRowId]);

  // Close filter / column popup on outside click
  const handleCloseFilter = useCallback(() => {
    setDateRange(appliedFilters.dateRange);
    setStatusOptions(appliedFilters.statusOptions);
    setIsFilterOpen(false);
  }, [appliedFilters, setDateRange, setStatusOptions]);

  const handleCloseColumnPopup = useCallback(() => {
    setIsColumnPopupOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        isFilterOpen &&
        filterContainerRef.current &&
        !filterContainerRef.current.contains(target) &&
        !(target as Element).closest?.('[data-date-picker-portal]')
      ) {
        handleCloseFilter();
      }
      if (
        isColumnPopupOpen &&
        columnContainerRef.current &&
        !columnContainerRef.current.contains(target)
      ) {
        handleCloseColumnPopup();
      }
    };
    if (isFilterOpen || isColumnPopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [
    isFilterOpen,
    isColumnPopupOpen,
    handleCloseFilter,
    handleCloseColumnPopup,
  ]);

  // Column visibility change
  const handleColumnVisibilityChange = (key: string, visible: boolean) => {
    const nextVisible = { ...visibleColumnKeys, [key]: visible };
    setVisibleColumnKeys(nextVisible);
    const colsString = buildColsParam(nextVisible);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (colsString === DEFAULT_COLS_STRING) {
          next.delete(COLUMNS_URL_PARAM);
        } else {
          next.set(COLUMNS_URL_PARAM, colsString);
        }
        return next;
      },
      { replace: true }
    );
  };

  // ---------------------------------------------------------------------------
  // Columns definition
  // ---------------------------------------------------------------------------

  const columns: Column<ClientListItem>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Imię i nazwisko',
        width: '256px',
        showSortIcon: true,
        render: (row) => {
          const profileImageUrl = getProfileImageUrl(row.logo_url);
          return (
            <div className="flex items-center w-full min-w-0 gap-3">
              <div className="w-8 h-8 rounded-full bg-[#E8EEF0] flex items-center justify-center shrink-0 overflow-hidden">
                {profileImageUrl ? (
                  <img
                    src={profileImageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    aria-label={row.name}
                  />
                ) : (
                  <span className="text-[#223746] text-base font-medium">
                    {getInitials(row.name)}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <Tooltip content={row.name}>
                  <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                    {row.name}
                  </span>
                </Tooltip>
              </div>
            </div>
          );
        },
      },
      {
        key: 'user_id',
        header: 'ID klienta',
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
            {row.user_id ?? '—'}
          </span>
        ),
      },
      {
        key: 'subscription_plan',
        header: 'Nazwa planu',
        render: (row) =>
          row.plan_name ? (
            <Tooltip content={row.plan_name}>
              <div className="inline-flex items-center overflow-hidden rounded-[8px] bg-[#EAF3FF] max-w-full">
                <div className="flex items-center min-w-0 px-3 py-[6px]">
                  <span className="text-[14px] font-medium leading-5 tracking-[0.1px] text-[#1665C2] truncate">
                    {row.plan_name}
                  </span>
                </div>
              </div>
            </Tooltip>
          ) : (
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px]">
              —
            </span>
          ),
      },
      {
        key: 'payment_status',
        header: 'Status płatności',
        render: (row) => <PaymentStatusBadge status={row.payment_status} />,
      },
      {
        key: 'created_at',
        header: 'Data utworzenia',
        width: '200px',
        showSortIcon: true,
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] whitespace-nowrap block">
            {formatDate(row.created_at)}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        showSortIcon: true,
        render: (row, index) => {
          const isDraft = row.status === 'draft';
          if (isDraft) {
            return (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium leading-4 tracking-[0.4px] bg-[#E8EEF0] text-[#474A4C]">
                Szkic
              </span>
            );
          }
          return (
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDialog(row);
              }}
              className="cursor-pointer inline-flex"
              data-testid={`toggle-status-${index}`}
            >
              <div style={{ pointerEvents: 'none' }}>
                <ToggleButton
                  checked={row.status === 'active'}
                  onChange={() => {}}
                  label={row.status === 'active' ? 'Aktywny' : 'Nieaktywny'}
                  size="Small"
                />
              </div>
            </div>
          );
        },
      },
      {
        key: 'actions',
        header: 'Akcje',
        width: '80px',
        stickyRight: true,
        stickyRightBg: '#ffffff',
        render: (row, index) => {
          const isOpen = openMenuRowId === row.id;
          return (
            <div className="inline-flex items-center justify-center w-full">
              <button
                ref={(el) => {
                  if (isOpen) {
                    menuTriggerRef.current = el;
                  }
                }}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = openMenuRowId !== row.id;
                  if (willOpen) {
                    menuTriggerRef.current = e.currentTarget;
                    const rect = (
                      e.currentTarget as HTMLButtonElement
                    ).getBoundingClientRect();
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const menuHeight = 100;
                    setMenuPosition({
                      top:
                        spaceBelow < menuHeight + 8
                          ? rect.top - menuHeight - 4
                          : rect.bottom + 4,
                      right: window.innerWidth - rect.right,
                    });
                  } else {
                    setMenuPosition(null);
                  }
                  setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-label={`Akcje dla klienta ${row.name}`}
                data-testid={`action-menu-button-${index ?? 0}`}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden
                >
                  <path
                    d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          );
        },
      },
    ],
    [openMenuRowId, handleOpenDialog]
  );

  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (col) => col.key === 'actions' || visibleColumnKeys[col.key] !== false
      ),
    [columns, visibleColumnKeys]
  );

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const pageOutOfRange =
    !loading && totalCount > 0 && page > Math.ceil(totalCount / limit);

  const hasNoData =
    !loading &&
    !isSearchClearing &&
    !pageOutOfRange &&
    page === 1 &&
    clients.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !search.trim() &&
    !appliedFilters.dateRange.startDate &&
    !appliedFilters.dateRange.endDate &&
    !appliedFilters.statusOptions.some((o) => o.checked);

  const handleApplyFiltersAndClose = () => {
    handleApplyFilters();
    setIsFilterOpen(false);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <div className="w-full h-full min-h-0 flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
        {/* Header */}
        <div
          className="flex items-center justify-between shrink-0"
          id="client-list-content"
        >
          <div className="text-[#181A1B] text-[22px] font-normal leading-7">
            Klienci
          </div>
          {!hasNoData && (
            <div className="flex flex-row gap-2">
              <button
                onClick={() => navigate('/Klienci/Uprawnienia')}
                className="h-10 bg-transparent px-4 py-2.5 border border-[#DCDDE0] flex items-center justify-center rounded-lg hover:bg-[#474A4C]/[0.08] transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-label="Uprawnienia"
                data-testid="Uprawnienia"
              >
                <span className="text-[#474A4C] text-sm font-medium leading-5 tracking-[0.1px]">
                  Uprawnienia
                </span>
              </button>
              <button
                onClick={() => navigate('/Klienci/Dodaj-klienta')}
                className="h-10 bg-[#2A4455] px-4 py-2.5 flex items-center justify-center rounded-lg hover:bg-[#1f3340] transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-label="Dodaj klienta"
                data-testid="Dodaj klienta"
              >
                <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                  Dodaj klienta
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Search + Filter + Column controls */}
        {!hasNoData && (
          <div className="flex items-center justify-between shrink-0">
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Szukaj po nazwie"
            />
            <div className="flex items-center gap-2">
              {/* Filter */}
              <div
                ref={filterContainerRef}
                className={`dropdown ${isFilterOpen ? 'show' : ''}`}
              >
                <style>{`
                  .dropdown { position: relative; }
                  .dropdown-content {
                    position: absolute;
                    background-color: #f9f9f9;
                    min-width: 130px;
                    box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
                    padding: 12px 16px;
                  }
                  .dropdown.show .dropdown-content { display: block; }
                `}</style>
                <FilterBar
                  onClick={() => setIsFilterOpen((prev) => !prev)}
                  isOpen={isFilterOpen}
                  filterCount={
                    (appliedFilters.dateRange.startDate ? 1 : 0) +
                    (appliedFilters.dateRange.endDate ? 1 : 0) +
                    appliedFilters.statusOptions.filter((o) => o.checked).length
                  }
                />
                <FilterPopup
                  isOpen={isFilterOpen}
                  onClose={handleCloseFilter}
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                  statusOptions={statusOptions}
                  onStatusChange={setStatusOptions}
                  onApply={handleApplyFiltersAndClose}
                  onClear={handleClearFilters}
                />
              </div>

              {/* Column customization */}
              <div ref={columnContainerRef} className="relative">
                <button
                  type="button"
                  onClick={() => {
                    handleCloseFilter();
                    setIsColumnPopupOpen((prev) => !prev);
                  }}
                  className="flex items-center justify-center rounded-lg border border-[#DCDDE0] px-[8px] py-[8px] cursor-pointer transition-all duration-200 ease-in-out hover:bg-[#E8EEF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                  aria-label="Kolumny"
                  aria-expanded={isColumnPopupOpen}
                  aria-haspopup="dialog"
                  data-testid="column-customization-button"
                >
                  <ViewColumnsIcon
                    className="h-6 w-6 text-on-surface-variant shrink-0"
                    aria-hidden
                  />
                </button>
                <ColumnCustomizationPopup
                  isOpen={isColumnPopupOpen}
                  onClose={handleCloseColumnPopup}
                  columnOptions={COLUMN_OPTIONS}
                  visibleKeys={visibleColumnKeys}
                  onVisibilityChange={handleColumnVisibilityChange}
                />
              </div>
            </div>
          </div>
        )}

        {/* Table area */}
        <div
          ref={tableScrollContainerRef}
          className="flex-1 min-h-0 overflow-x-auto overflow-y-auto"
        >
          {clients.length === 0 && !loading && !isSearchClearing ? (
            hasNoData ? (
              /* Empty state — no clients exist yet */
              <div className="flex flex-col items-center justify-center w-full h-full">
                <LottieAnimation
                  animationData={noDataAnimation}
                  width={277}
                  aspectRatio="1 / 1"
                />
                <div className="flex flex-col items-center justify-center gap-5">
                  <div className="flex flex-col items-start justify-start gap-1">
                    <div className="w-[548px] max-w-full text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                      Brak klientów.
                    </div>
                    <div className="w-[548px] max-w-full text-center text-[#474A4C] text-base font-normal leading-6 tracking-[0.5px]">
                      Dodaj klienta, aby rozpocząć zarządzanie organizacjami
                      klientów.
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/Klienci/Dodaj-klienta')}
                    className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                    aria-label="Dodaj klienta"
                  >
                    <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                      Dodaj klienta
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              /* No results state — search/filter applied but no matches */
              <div className="flex flex-col items-center justify-center w-full h-full gap-3">
                <LottieAnimation
                  animationData={noResultsAnimation}
                  width="clamp(160px, 18vw, 360px)"
                  aspectRatio="432 / 350"
                />
                <div className="w-[548px] max-w-full h-12 flex flex-col items-center justify-start gap-1">
                  <div className="w-full text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                    Nie znaleziono wyników.
                  </div>
                  <div className="w-full text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                    {(() => {
                      const hasSearch = search.trim() !== '';
                      const hasFilter =
                        appliedFilters.statusOptions.some((o) => o.checked) ||
                        !!appliedFilters.dateRange.startDate ||
                        !!appliedFilters.dateRange.endDate;
                      if (hasSearch && hasFilter) {
                        return 'Spróbuj zmienić kryteria wyszukiwania lub filtry.';
                      }
                      if (hasSearch) {
                        return 'Spróbuj zmienić kryteria wyszukiwania.';
                      }
                      if (hasFilter) {
                        return 'Spróbuj zmienić ustawienia filtrów.';
                      }
                      return 'Spróbuj zmienić kryteria wyszukiwania lub filtry.';
                    })()}
                  </div>
                </div>
              </div>
            )
          ) : (
            <Table
              columns={visibleColumns}
              data={clients}
              loading={loading}
              loadingRows={limit}
              stickyHeader
              getRowKey={(row) => row.id}
              onSort={handleSort}
              {...(sortColumn != null && {
                sortColumn,
                sortDirection: sortOrder,
              })}
              getStickyCellZIndex={(row, _idx, columnKey) =>
                columnKey === 'actions' && openMenuRowId === row.id ? 50 : 1
              }
            />
          )}
        </div>

        {/* Pagination */}
        {totalCount > limit && (
          <div className="shrink-0 px-4 -mx-4">
            <Pagination
              page={page}
              limit={limit}
              totalCount={totalCount}
              onPageChange={setPage}
              onLimitChange={handleLimitChange}
            />
            {limitError && (
              <div className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
                {limitError}
              </div>
            )}
          </div>
        )}

        {/* Snackbars */}
        <Snackbar
          open={showAddSuccessSnackbar}
          message="Klient został pomyślnie dodany. Wiadomość z tymczasowymi danymi do logowania została wysłana na jego skrzynkę odbiorczą."
          variant="success"
          onClose={() => setShowAddSuccessSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton
        />
        <Snackbar
          open={showEditSuccessSnackbar}
          message="Dane klienta zostały pomyślnie zaktualizowane."
          variant="success"
          onClose={() => setShowEditSuccessSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton
        />
        <Snackbar
          open={showStatusSuccessSnackbar}
          message={statusSuccessMessage}
          variant="success"
          onClose={() => setShowStatusSuccessSnackbar(false)}
          autoHideDuration={15000}
          position="top-center"
          showCloseButton
        />
        <Snackbar
          open={showStatusErrorSnackbar}
          message={statusErrorMessage}
          variant="error"
          onClose={() => setShowStatusErrorSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton
        />

        {/* Activate / Deactivate Dialog */}
        {selectedClient && selectedClient.status !== 'draft' && (
          <UpdateStatusDialog
            open={isDialogOpen}
            onClose={handleDialogClose}
            onConfirm={handleConfirmStatusUpdate}
            isSubmitting={isSubmitting}
            {...getClientStatusDialogConfig(
              selectedClient.name,
              selectedClient.status === 'active'
            )}
          />
        )}
      </div>

      {/* Actions menu portal — renders outside overflow container so it never clips */}
      {openMenuRowId !== null &&
        menuPosition !== null &&
        createPortal(
          <div
            ref={menuPortalRef}
            style={{
              position: 'fixed',
              top: menuPosition.top,
              right: menuPosition.right,
              zIndex: 9999,
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Tab') {
                return;
              }
              const items =
                menuPortalRef.current?.querySelectorAll<HTMLElement>(
                  '[role="menuitem"]'
                );
              if (!items) {
                return;
              }
              const lastItem = items[items.length - 1];
              if (
                lastItem &&
                document.activeElement === lastItem &&
                !e.shiftKey
              ) {
                setOpenMenuRowId(null);
                setMenuPosition(null);
              }
            }}
          >
            <Menu
              items={[
                { id: 'VIEW', label: 'Podgląd' },
                { id: 'EDIT', label: 'Edytuj' },
              ]}
              variant={MenuVariant.Light}
              size={MenuSize.Small}
              toolTip={false}
              onItemClick={(itemId) => {
                const rowId = openMenuRowId;
                setOpenMenuRowId(null);
                setMenuPosition(null);
                const row = clients.find((c) => c.id === rowId);
                if (!row) {
                  return;
                }
                if (itemId === 'VIEW') {
                  navigate(`/Klienci/Podgląd-klienta/${row.id}`);
                } else if (itemId === 'EDIT') {
                  navigate(`/Klienci/Edycja-klienta/${row.id}`);
                }
              }}
              showLeadingIcons={false}
            />
          </div>,
          document.body
        )}
    </>
  );
}
